import type { CompanyDossier } from "./types";

export type BoothGroup = NonNullable<CompanyDossier["booth_group"]>;
export type BoothGroupAccount = BoothGroup["accounts"][number];

const groupCache = new Map<string, BoothGroup>();

export function boothGroupCacheKey(eventKey: string, boothNumber: string): string {
  return `${eventKey.trim()}::${boothNumber.trim()}`;
}

export function clearSharedBoothGroupCache(): void {
  groupCache.clear();
}

export function cachedBoothGroup(eventKey: string, boothNumber: string): BoothGroup | undefined {
  return groupCache.get(boothGroupCacheKey(eventKey, boothNumber));
}

export function rememberBoothGroup(eventKey: string, group: BoothGroup): BoothGroup {
  if (usableBoothGroup(group)) {
    groupCache.set(boothGroupCacheKey(eventKey, group.booth_number), group);
  }
  return group;
}

export function usableBoothGroup(group: BoothGroup | null | undefined): group is BoothGroup {
  return Boolean(group && group.booth_number.trim() && group.accounts.length > 1);
}

export function shortCompanyChipLabel(name: string | null | undefined): string {
  const raw = (name ?? "").trim();
  if (!raw) return "Account";
  const stripped = raw.replace(/,?\s+(inc|incorporated|llc|ltd|limited|corp|corporation|co)\.?\s*$/i, "").trim();
  return stripped || raw;
}

export function foldCompanyName(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\b(inc|incorporated|llc|ltd|limited|corp|corporation|co|company)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function namesMatch(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = foldCompanyName(a ?? "");
  const right = foldCompanyName(b ?? "");
  return Boolean(left && right && left === right);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function asText(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => asText(item)).filter((item): item is string => Boolean(item));
}

function resultRows(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) {
    return value.map(asRecord).filter((row): row is Record<string, unknown> => Boolean(row));
  }
  const rec = asRecord(value);
  if (rec && Array.isArray(rec.results)) {
    return rec.results.map(asRecord).filter((row): row is Record<string, unknown> => Boolean(row));
  }
  return [];
}

function boothOf(row: Record<string, unknown>): string | null {
  const direct = asText(row.booth) ?? asText(row.booth_number);
  if (direct) return direct;
  const numbers = asStringList(row.booth_numbers);
  return numbers[0] ?? null;
}

export function catalogPrimaryName(dossier: CompanyDossier): string | null {
  for (const booth of dossier.booths) {
    const rec = asRecord(booth);
    if (!rec) continue;
    const meta = asRecord(rec.metadata);
    const name = asText(meta?.mys_exhname) ?? asText(rec.company_name) ?? asText(rec.exhibitor_name);
    if (name) return name;
  }
  return null;
}

export function catalogNameFromBoothGet(payload: unknown): string | null {
  const rec = asRecord(payload);
  const booth = asRecord(rec?.booth) ?? rec;
  if (!booth) return null;
  const meta = asRecord(booth.metadata);
  return asText(meta?.mys_exhname) ?? asText(booth.company_name) ?? asText(booth.exhibitor_name);
}

export function boothNumberFromDossier(dossier: CompanyDossier, fallback?: string | null): string | null {
  const fromFallback = asText(fallback);
  if (fromFallback) return fromFallback;
  const fromGroup = asText(dossier.booth_group?.booth_number);
  if (fromGroup) return fromGroup;
  const fromResolution = dossier.resolution.booth_numbers.map((item) => asText(item)).find(Boolean);
  return fromResolution ?? null;
}

export function intelSharedBooth(intel: unknown): {
  shared: boolean;
  peers: string[];
  accountId: string | null;
  name: string | null;
} {
  const payload = asRecord(intel);
  const account = asRecord(payload?.account) ?? payload;
  const raw = asRecord(account?.raw_attributes);
  const peers = asStringList(raw?.shared_booth_peers);
  const flagged = raw?.shared_booth === true;
  return {
    shared: flagged || peers.length > 0,
    peers,
    accountId: asText(account?.account_id),
    name: asText(account?.name),
  };
}

export function pickAccountSearchHit(
  results: unknown,
  boothNumber: string,
  queryName: string,
): BoothGroupAccount | null {
  const rows = resultRows(results);
  const sameBooth = rows.filter((row) => boothOf(row) === boothNumber);
  const pool = sameBooth.length ? sameBooth : rows;
  const ranked = [...pool].sort((left, right) => {
    const leftName = asText(left.name) ?? "";
    const rightName = asText(right.name) ?? "";
    if (namesMatch(leftName, queryName) !== namesMatch(rightName, queryName)) {
      return namesMatch(leftName, queryName) ? -1 : 1;
    }
    const leftFold = foldCompanyName(leftName);
    const rightFold = foldCompanyName(rightName);
    const queryFold = foldCompanyName(queryName);
    const leftClose = leftFold.startsWith(queryFold) || queryFold.startsWith(leftFold);
    const rightClose = rightFold.startsWith(queryFold) || queryFold.startsWith(rightFold);
    if (leftClose !== rightClose) return leftClose ? -1 : 1;
    return 0;
  });
  const hit = ranked[0];
  const accountId = asText(hit?.account_id);
  if (!hit || !accountId) return null;
  return { account_id: accountId, name: asText(hit.name), is_primary: false };
}

export function assembleBoothGroup(input: {
  boothNumber: string;
  current: { account_id: string; name: string | null };
  peers: Array<{ account_id: string; name: string | null }>;
  primaryName?: string | null;
}): BoothGroup {
  const byId = new Map<string, BoothGroupAccount>();
  const add = (row: { account_id: string; name: string | null }, explicitPrimary?: boolean) => {
    const id = row.account_id.trim();
    if (!id) return;
    const isPrimary = explicitPrimary ?? namesMatch(row.name, input.primaryName);
    const prior = byId.get(id);
    byId.set(id, {
      account_id: id,
      name: row.name ?? prior?.name ?? null,
      is_primary: Boolean(prior?.is_primary || isPrimary),
    });
  };
  add(input.current);
  for (const peer of input.peers) add(peer);
  if (![...byId.values()].some((row) => row.is_primary) && input.current.account_id) {
    const first = byId.get(input.current.account_id);
    if (first) first.is_primary = byId.size === 1;
  }
  const accounts = [...byId.values()].sort((left, right) => {
    if (left.is_primary !== right.is_primary) return left.is_primary ? -1 : 1;
    return (left.name ?? "").localeCompare(right.name ?? "", undefined, { sensitivity: "base" });
  });
  return { booth_number: input.boothNumber, accounts };
}

export function boothGroupFromEventList(
  results: unknown,
  boothNumber: string,
  primaryName?: string | null,
): BoothGroup | null {
  const accounts: BoothGroupAccount[] = [];
  for (const row of resultRows(results)) {
    if ((asText(row.booth_number) ?? boothNumber) !== boothNumber) continue;
    const accountId = asText(row.account_id);
    if (!accountId) continue;
    accounts.push({
      account_id: accountId,
      name: asText(row.name),
      is_primary: row.is_primary_booth_exhibitor === true || namesMatch(asText(row.name), primaryName),
    });
  }
  const group = { booth_number: boothNumber, accounts };
  return usableBoothGroup(group) ? group : null;
}

export async function resolveSharedBoothGroup(opts: {
  eventKey: string;
  dossier: CompanyDossier;
  intel?: unknown;
  boothNumber?: string | null;
  searchAccounts: (name: string) => Promise<unknown>;
  listBoothEvents?: (boothNumber: string) => Promise<unknown>;
  getCatalogBooth?: (boothNumber: string) => Promise<unknown>;
}): Promise<CompanyDossier> {
  const boothNumber = boothNumberFromDossier(opts.dossier, opts.boothNumber);
  if (usableBoothGroup(opts.dossier.booth_group)) {
    rememberBoothGroup(opts.eventKey, opts.dossier.booth_group);
    return opts.dossier;
  }
  if (boothNumber) {
    const cached = cachedBoothGroup(opts.eventKey, boothNumber);
    if (cached) return { ...opts.dossier, booth_group: cached };
  }

  const signal = intelSharedBooth(opts.intel);
  const currentId = signal.accountId ?? opts.dossier.resolution.account_id;
  const currentName = signal.name ?? opts.dossier.account?.name ?? null;
  let primaryName = catalogPrimaryName(opts.dossier);
  if (signal.shared && !primaryName && boothNumber && opts.getCatalogBooth) {
    try {
      primaryName = catalogNameFromBoothGet(await opts.getCatalogBooth(boothNumber));
    } catch {
      primaryName = null;
    }
  }

  if (signal.shared && boothNumber && currentId) {
    const peerHits = await Promise.all(
      signal.peers.map(async (peer) => {
        try {
          return pickAccountSearchHit(await opts.searchAccounts(peer), boothNumber, peer);
        } catch {
          return null;
        }
      }),
    );
    const group = assembleBoothGroup({
      boothNumber,
      current: { account_id: currentId, name: currentName },
      peers: peerHits.filter((row): row is BoothGroupAccount => Boolean(row)),
      primaryName,
    });
    if (usableBoothGroup(group)) {
      rememberBoothGroup(opts.eventKey, group);
      return { ...opts.dossier, booth_group: group };
    }
  }

  if (boothNumber && opts.listBoothEvents) {
    try {
      const listed = boothGroupFromEventList(await opts.listBoothEvents(boothNumber), boothNumber, primaryName);
      if (listed) {
        rememberBoothGroup(opts.eventKey, listed);
        return { ...opts.dossier, booth_group: listed };
      }
    } catch {
      // Live MCP 0.8.0 does not expose account_event_list.
    }
  }

  return opts.dossier;
}
